import { FC } from 'react';
import styled from 'styled-components';
import { stringFormat, T } from 'src/shared/helpers';
import usePreview3DStore from 'src/components/widgets/preview3D/store';
import { ObjectsIcon } from 'src/components/widgets/icons/scene';
import { panel, PropertiesPanelResourceProperty, PropertiesPanelSwatch } from '../../scene-properties-panel';
import { readMaterialPreview } from '../../scene-editors/materialPreview';
import { getRetargetObjectsAndMaterials, retargetMapToActions } from '../../../configurator-composer/composer/helpers/composerHelpers';
import { ComposerFormStack, ComposerSection, ComposerSettingsBody, ComposerUnsupportedNote } from '../composerWorkspace';
import { ComposerSlotGlyph } from '../composerSlots';
import { ComposerOptionActionNode } from './composerAttributesModels';

// IL RETARGET È UNA TAB, NON UNA FINESTRA (round 4, pacchetto I; il prototipo la chiama Bindings, noi
// teniamo il nostro nome). Elenca gli oggetti e i materiali che le azioni di questa cosa — e di ciò che
// contiene — usano, uno per riga con il numero di usi: cambiare la riga li ripunta tutti in un colpo.
// Prima si apriva una finestra con «Used now → Replace with» e un Confirm in fondo.
//
// OGNI RIGA È UNO SLOT (utente, 2026-10-15; prototipo V2): il glifo dell'oggetto o la sfera del
// materiale, il nome, «N uses» sotto, e la riga apre l'elenco con cui sostituirlo. Era un nome a
// sinistra e una tendina a destra.

const Slots = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

type SceneItem = { id: string; name?: string };

const usesOf = (actions: ComposerOptionActionNode[], id: string) =>
    actions.filter((action) =>
        [action.selector, action.value].some((field) =>
            (field || '')
                .split(';')
                .map((entry) => entry.trim())
                .includes(id)
        )
    ).length;

const ComposerRetargetPanel: FC<{
    actions: ComposerOptionActionNode[];
    objects: SceneItem[];
    materials: SceneItem[];
    subjectLabel: string;
    onApply: (remapped: ComposerOptionActionNode[]) => void;
}> = ({ actions, objects, materials, subjectLabel, onApply }) => {
    const sceneEditor = usePreview3DStore((store) => store.sceneEditor);
    const { objectIds, materialIds } = getRetargetObjectsAndMaterials(actions);

    // Un materiale sta nel valore delle azioni che lo mettono su un oggetto, e nel selettore di quelle
    // che lo ricolorano: si ripunta in tutt'e due i posti. Un oggetto sta solo nel selettore.
    const replace = (kind: 'object' | 'material', from: string, to: string) => {
        if (!to || to === from) return;
        const map = { [from]: to };
        onApply(retargetMapToActions(actions, kind === 'material' ? map : undefined, map));
    };

    const objectGlyph = (
        <ComposerSlotGlyph>
            <ObjectsIcon />
        </ComposerSlotGlyph>
    );
    const materialFace = (id: string) => <PropertiesPanelSwatch $url={readMaterialPreview(sceneEditor, id) ?? undefined} $tint={panel.kindAsset} $size={26} />;

    const section = (kind: 'object' | 'material', title: string, used: string[], list: SceneItem[]) => {
        if (!used.length) return null;
        const options = list.map((item) =>
            kind === 'object'
                ? { value: item.id, label: item.name || item.id, mark: objectGlyph }
                : { value: item.id, label: item.name || item.id, previewUrl: readMaterialPreview(sceneEditor, item.id) ?? undefined }
        );
        return (
            <ComposerSection id={`retarget.${kind}`} title={title}>
                <Slots>
                    {used.map((id) => {
                        const uses = usesOf(actions, id);
                        const current = list.find((item) => item.id === id || item.name === id);
                        return (
                            <PropertiesPanelResourceProperty
                                key={id}
                                id={`composer-retarget-${kind}-${id}`}
                                preview={kind === 'object' ? objectGlyph : materialFace(current?.id ?? id)}
                                name={current?.name || id}
                                caption={stringFormat(uses === 1 ? T._('{0} use', 'Admin') : T._('{0} uses', 'Admin'), uses.toString())}
                                options={options}
                                value={current?.id ?? id}
                                optionTint={kind === 'material' ? panel.kindAsset : undefined}
                                searchPlaceholder={kind === 'object' ? T._('Search objects…', 'Admin') : T._('Search materials...', 'Admin')}
                                onSelect={(chosen) => replace(kind, id, String(chosen))}
                            />
                        );
                    })}
                </Slots>
            </ComposerSection>
        );
    };

    return (
        <ComposerSettingsBody>
            <ComposerFormStack>
                {objectIds.length + materialIds.length === 0 ? (
                    <ComposerUnsupportedNote>{stringFormat(T._('{0} uses no objects or materials yet.', 'Admin'), subjectLabel)}</ComposerUnsupportedNote>
                ) : (
                    <>
                        {section('object', T._('Objects', 'Admin'), objectIds, objects)}
                        {section('material', T._('Materials', 'Admin'), materialIds, materials)}
                    </>
                )}
            </ComposerFormStack>
        </ComposerSettingsBody>
    );
};

export default ComposerRetargetPanel;
